import { Card, MenuItem, Collapse, IconButton, makeStyles } from '@material-ui/core'
import StyledTable from '../Common/StyledTable'
import MoreMenu from '../Common/Menus/MoreMenu'
import { gray } from '../../utils/colors'
import { useEffect, useState, useMemo } from 'react'
import { ExpandLessOutlined, ExpandMoreOutlined } from '@material-ui/icons'
import { MTableCell } from 'material-table'
import clsx from 'clsx'
import StyledChip from '../Common/StyledChip'
import ClientNameLink from '../Common/ClientNameLink'
import PetNameLink from '../Common/PetNameLink'
import useSelectedFacility from '../../hooks/useSelectedFacility'
import {
	TRAINING,
	standardizeStartOfDateUTC,
	convert24HourTimeTo12HourTime,
	TRAINING_ATTENDANCE_STATUSES
} from '../../utils'
import { Link as RouterLink } from 'react-router-dom'
import TrainingSessionEditDialog from '../Dialogs/Training/TrainingSessionEditDialog'
import { MainButton } from '../Button/MainButton'
import moment from 'moment-timezone'

const useStyles = makeStyles((theme) => ({
	root: { marginTop: '1.5rem', marginBottom: '1.5rem' },
	sessionsContainer: { margin: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' },
	sessionContainer: { display: 'flex', flexDirection: 'column', gap: '1rem' },
	sessionHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		borderWidth: '1px',
		borderStyle: 'solid',
		borderColor: gray[400],
		backgroundColor: gray[100],
		borderRadius: '0.375rem',
		borderBottomLeftRadius: '0.375rem',
		borderBottomRightRadius: '0.375rem',
		paddingLeft: '1rem'
	},
	sessionTitle: { fontWeight: 800, fontSize: '1rem', padding: 0, margin: 0 },
	sessionSubTitle: { fontWeight: 600, fontSize: '1rem', padding: '0 1rem', margin: 0 },
	sessionHeaderOpen: {
		borderColor: gray[200],
		backgroundColor: gray[200],
		borderBottomLeftRadius: 0,
		borderBottomRightRadius: 0
	},
	sessionTableCell: {
		padding: '.5rem 1rem',
		backgroundColor: gray[200],
		border: 0
	},
	attendeeListHeaderContainer: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center'
	}
}))

const statusColors = {
	1: 'purple',
	4: 'green',
	7: 'gray',
	8: 'red',
	9: 'gray'
}

const sessionIsInThePast = (trainingClass, session, timeZone) => {
	if (!trainingClass || !session) {
		return false
	}
	const nowInTz = moment().tz(timeZone)
	let [startHours, startMinutes] = trainingClass.start_time.split(':')
	const sessionStart = standardizeStartOfDateUTC(session.date)
	const sessionStartDateTime = nowInTz.clone().set({
		month: sessionStart.month(),
		date: sessionStart.date(),
		year: sessionStart.year(),
		hours: startHours,
		minutes: startMinutes,
		seconds: 0,
		milliseconds: 0
	})
	return nowInTz.isAfter(sessionStartDateTime)
}

const TrainingClassSessionListCard = ({ trainingClass, date, classStatus, onRemoveAttendee }) => {
	const classes = useStyles()

	const { facility } = useSelectedFacility()

	const [sessionExpanded, setSessionExpanded] = useState({})
	const [editSessionDialogOpen, setEditSessionDialogOpen] = useState(false)
	const [currentEditedSessionObject, setCurrentEditedSessionObject] = useState({})
	const [currentEditedSessionNumber, setCurrentEditedSessionNumber] = useState(0)

	useEffect(() => {
		if (currentEditedSessionNumber === 0) {
			setCurrentEditedSessionObject({})
		}
		if (trainingClass) {
			const requestedSession = trainingClass.attendance_by_session.find(
				(sesh) => sesh.session === currentEditedSessionNumber
			)

			if (requestedSession) {
				const editable = {
					...requestedSession,
					start_time: trainingClass.start_time,
					session_date: requestedSession.date,
					instructor_id: trainingClass.instructor_id?._id
				}
				if (requestedSession.adjustments) {
					editable.adjustment_id = requestedSession.adjustments._id
					editable.session_date = requestedSession.adjustments.new_date || editable.date
					editable.start_time = requestedSession.adjustments.new_start_time || editable.start_time
					editable.instructor_id =
						requestedSession.adjustments.new_instructor?._id || editable.instructor_id
				}

				setCurrentEditedSessionObject(editable)
				setEditSessionDialogOpen(true)
			} else {
				setCurrentEditedSessionObject({})
			}
		}
	}, [trainingClass, currentEditedSessionNumber, setCurrentEditedSessionObject, setEditSessionDialogOpen])

	const isPrivate = trainingClass?.training_course_id?.categories?.includes('private') || false

	const handleExpandClick = (session) => {
		setSessionExpanded({ ...sessionExpanded, [session]: !sessionExpanded[session] })
	}

	const timeZone = useMemo(() => {
		return facility?.market?.time_zone || facility?.time_zone || 'America/New_York'
	})

	const handleEditSessionRequest = (session_number) => {
		setCurrentEditedSessionNumber(session_number)
	}

	const handleEditSessionDialogClose = () => {
		setCurrentEditedSessionNumber(0)
		setEditSessionDialogOpen(false)
	}

	useEffect(() => {
		if (trainingClass) {
			const expanded = {}
			trainingClass.attendance_by_session.forEach((sesh) => {
				let adjustedDate = null
				if (sesh.adjustments && sesh.adjustments.new_date) {
					adjustedDate = sesh.adjustments.new_date
				}

				const standardizedDate = adjustedDate
					? standardizeStartOfDateUTC(adjustedDate)
					: standardizeStartOfDateUTC(date)
				const formatDate = standardizedDate.format('YYYY-MM-DD')
				expanded[formatDate] = formatDate === date ? true : false
			})
			setSessionExpanded(expanded)
		}
	}, [trainingClass, date])

	const classCancelled = useMemo(() => {
		if (!trainingClass) {
			return false
		}
		return trainingClass.cancelled
	}, [trainingClass])

	// const handleOnRemoveAttendee = (attendanceRecord) => {
	// 	if (onRemoveAttendee) {
	// 		onRemoveAttendee(attendanceRecord)
	// 	}
	// }

	const columns = [
		{
			title: 'Client',
			width: '20%',
			sorting: false,
			render: (attendance) => {
				return (
					<div style={{ display: 'flex', flexDirection: 'column' }}>
						{attendance.handlers?.map((handler, i) => {
							return (
								<ClientNameLink
									key={`${handler.household}-handler-${i}`}
									household_id={handler.household}
									accounts={[handler]}
									usePrimary={false}
								/>
							)
						})}
					</div>
				)
			}
		},
		{
			title: 'Pet',
			width: '20%',
			sorting: false,
			render: (attendance) => {
				const pet = attendance.pet[0]
				return <PetNameLink pet={pet} showIssues visit={{ type: TRAINING }} />
			}
		},
		{
			title: 'Training Tags',
			sorting: false,
			render: (attendance) => {
				const pet = attendance.pet[0]
				return (
					<div>
						{pet?.tags?.map((tag) => (
							<StyledChip key={tag.name} useThemeProvider label={tag.name} />
						))}
					</div>
				)
			}
		},
		{
			title: 'Status',
			sorting: false,
			width: '10%',
			render: (attendance) => {
				const { status } = attendance
				const { name, id } = status

				return <StyledChip skiptownvariant={statusColors[id]} label={name} />
			}
		},
		{
			title: '',
			width: 50,
			sorting: false,
			render: (d) => {
				const { training_enrollment = [] } = d
				const menuOptions = []
				menuOptions.push(
					<MenuItem
						key="edit-location"
						component={RouterLink}
						to={`/facilities/${facility?._id}/training/class/${trainingClass?._id}/enrollment/${training_enrollment[0]?._id}`}
						onClick={() => console.log(d)}>
						View Enrollment Details
					</MenuItem>
				)
				// menuOptions.push(
				// 	<MenuItem key="remove-location" onClick={() => handleOnRemoveAttendee(d)}>
				// 		Remove Attendee
				// 	</MenuItem>
				// )
				return classCancelled ? null : (
					<MoreMenu options={menuOptions} description="Attendee" useHoverState />
				)
			}
		}
	]

	return (
		<>
			<TrainingSessionEditDialog
				open={editSessionDialogOpen}
				onClose={handleEditSessionDialogClose}
				onSaved={handleEditSessionDialogClose}
				onCancelled={handleEditSessionDialogClose}
				session={currentEditedSessionObject}
				trainingClass={trainingClass}
			/>
			<Card className={classes.root}>
				{trainingClass ? (
					<div className={classes.sessionsContainer}>
						{trainingClass.attendance_by_session.map((sesh) => {
							const { session, date, attendance, adjustments = null } = sesh
							let adjustedTime = false

							if (adjustments) {
								adjustedTime = adjustments.new_start_time
							}

							const standardizedDate = standardizeStartOfDateUTC(date)
							const dateKey = standardizedDate.format('YYYY-MM-DD')
							const headerDate = standardizedDate.format('MMM DD, YYYY')
							let sessionHeaderClasses = [classes.sessionHeader]
							if (sessionExpanded[dateKey]) {
								sessionHeaderClasses.push(classes.sessionHeaderOpen)
							}
							const inPast = sessionIsInThePast(
								{
									...trainingClass,
									start_time: adjustedTime
										? adjustments.new_start_time
										: trainingClass.start_time,
									end_time: adjustedTime ? adjustments.new_end_time : trainingClass.end_time
								},
								{
									...sesh,
									date: sesh.date
								},
								timeZone
							)

							// only using with private classes, should only have 1 enrollment
							// and if that one (all) is checked in, then there is no need to be
							// able to edit this session because the client attended.
							const hasAllCheckedInStatuses = attendance.every(
								(attendee) => attendee.status?.id === TRAINING_ATTENDANCE_STATUSES.CHECKED_IN
							)

							const disableAbilityToEditSession = isPrivate
								? classCancelled || hasAllCheckedInStatuses
								: classCancelled || classStatus?.isAfter || inPast

							return (
								<div key={`${trainingClass._id}-session-${session}`}>
									<div className={clsx(sessionHeaderClasses)}>
										<h5 className={classes.sessionTitle}>
											Session: {session} - {headerDate} ({attendance.length} Attendees)
											{adjustments?.new_start_time ? (
												<span>
													{' '}
													{convert24HourTimeTo12HourTime(adjustments.new_start_time)}-
													{convert24HourTimeTo12HourTime(adjustments.new_end_time)}
												</span>
											) : null}
										</h5>

										<IconButton onClick={() => handleExpandClick(dateKey)}>
											{sessionExpanded[dateKey] ? (
												<ExpandLessOutlined style={{ fill: gray[700] }} />
											) : (
												<ExpandMoreOutlined style={{ fill: gray[700] }} />
											)}
										</IconButton>
									</div>

									<Collapse in={sessionExpanded[dateKey]} timeout="auto">
										{sessionExpanded[dateKey] ? (
											<div
												style={{
													display: 'flex',
													flexDirection: 'column',
													gap: '1rem',
													paddingTop: '1rem',
													backgroundColor: sessionExpanded[dateKey]
														? gray[200]
														: gray[100]
												}}>
												<div className={classes.attendeeListHeaderContainer}>
													<h6 className={classes.sessionSubTitle}>Attendee List</h6>
													<MainButton
														label="Edit Session"
														variant="text"
														disabled={disableAbilityToEditSession}
														onClick={() => handleEditSessionRequest(session)}
														style={{
															padding: `0 .5rem`,
															fontSize: '.85rem',
															textTransform: 'unset'
														}}
													/>
												</div>
												<StyledTable
													data={attendance}
													columns={columns}
													style={{
														boxShadow: 'none'
													}}
													headerStyle={{
														backgroundColor: gray[200],
														borderTop: 'none'
													}}
													hideBorderBottom
													overrideOptions={{ paging: false }}
													components={{
														Cell: (props) => {
															return (
																<MTableCell
																	className={classes.sessionTableCell}
																	{...props}
																/>
															)
														}
													}}
												/>
											</div>
										) : null}
									</Collapse>
								</div>
							)
						})}
					</div>
				) : null}
			</Card>
		</>
	)
}

export default TrainingClassSessionListCard
